import React from 'react';
import PropTypes from 'prop-types';
import CarouselItem from './CarouselItem';
import Navigation from './Navigation';
import resizeObserver from './resizeObserver';

import { Icon } from 'edcom-ui';

import styles from './Carousel.module.less';

// TODO convert to FC
export default class Carousel extends React.Component {
  constructor(props) {
    super(props);

    this.carouselIntervalTime = 5000;
    this.carouselRef = React.createRef();

    this.state = {
      translateValue: 0,
      activeIndex: 0,
      width: 0,
    };

    this.animatingScroll = false;
    this.intervalPointer = null;
    this.manuallyPaused = false;
    this.currentlyPaused = true;
  }

  listener = () => {
    this.toggleCarouselPlay();
  };

  componentDidMount = () => {
    this.startCarousel();
    this.setState({ width: this.carouselRef.current.offsetWidth });

    if (resizeObserver.observe) {
      resizeObserver.observe(this.carouselRef.current, () => {
        this.setState({ width: this.carouselRef.current.offsetWidth });
      });
    }
    window.addEventListener('scroll', this.listener);
  };

  componentWillUnmount = () => {
    if (resizeObserver.unobserve) {
      resizeObserver.unobserve(this.carouselRef.current);
    }
    window.removeEventListener('scroll', this.listener);
  };

  render() {
    let counter = 0;
    let items = this.props.items.map(i => {
      let lazyLoad = true;
      if (counter === 0) {
        lazyLoad = false;
      }

      counter++;

      return (
        <CarouselItem
          key={i.primaryImage}
          primaryImage={i.primaryImage}
          secondaryImage={i.secondaryImage}
          title={i.title}
          description={i.description}
          clickHandler={this.props.clickHandler}
          touchStartCallback={this.touchStartCallback}
          touchMoveCallback={this.touchMoveCallback}
          lazyLoad={lazyLoad}
        />
      );
    });

    let navigation = this.props.items.map((i, index) => {
      return (
        <Navigation
          key={i.primaryImage}
          index={index}
          active={index === this.state.activeIndex}
          callback={this.moveToIndex}
        />
      );
    });

    return (
      <div className={styles.carousel} ref={this.carouselRef}>
        {items.length > 1 && (
          <div className={styles.arrows}>
            <div
              className={`${styles.arrowNav} ${styles.leftNav}`}
              onClick={() => this.moveLeftManually()}
            >
              <Icon name="left" className={styles.iconLeftNav} />
            </div>
            <div
              className={`${styles.arrowNav} ${styles.rightNav}`}
              onClick={() => this.moveRightManually()}
            >
              <Icon name="right" />
            </div>
          </div>
        )}
        <div className={styles.carouselItemsWrapper}>
          <div
            className={styles.carouselItems}
            style={{ transform: `translateX(${this.state.translateValue}px)` }}
            onTransitionEnd={() => this.transitionEndCallback()}
            onClick={this.props.clickHandler}
          >
            {items}
          </div>
        </div>
        {items.length > 1 && (
          <div className={styles.navigation}>{navigation}</div>
        )}
      </div>
    );
  }

  startCarousel = () => {
    if (!this.manuallyPaused && this.currentlyPaused) {
      let self = this;
      this.currentlyPaused = false;
      this.intervalPointer = setInterval(function() {
        self.moveRight();
      }, this.carouselIntervalTime);
    }
  };

  stopCarousel = () => {
    this.currentlyPaused = true;
    clearInterval(this.intervalPointer);
  };

  stopCarouselManually = () => {
    this.manuallyPaused = true;
    this.stopCarousel();
  };

  toggleCarouselPlay = () => {
    if (this.manuallyPaused) {
      return false;
    }

    if (this.isCarouselInViewport()) {
      this.startCarousel();
    } else {
      this.stopCarousel();
    }
  };

  moveLeft = () => {
    this.move('left');
  };

  moveRight = () => {
    this.move('right');
  };

  moveLeftManually = () => {
    this.moveLeft();
    this.moveManually();
  };

  moveRightManually = () => {
    this.moveRight();
    this.moveManually();
  };

  moveManually = () => {
    this.manuallyPaused = true;
    this.stopCarousel();
  };

  move = direction => {
    if (this.animatingScroll) {
      return;
    }

    let activeIndex = this.state.activeIndex;
    let delta = null;

    this.animatingScroll = true;

    if (direction === 'left') {
      delta = this.state.translateValue + this.state.width;
      //Keeping the carousel cyclical - moving to the right most item if we are trying to move left past the first item.
      if (delta > 0) {
        delta = -1 * this.state.width * (this.props.items.length - 1);
        activeIndex = this.props.items.length - 1;
      } else {
        activeIndex--;
      }
    } else {
      delta = this.state.translateValue - this.state.width;
      //Keeping the carousel cyclical - moving to the left most item if we are trying to move right past the last item.
      if (Math.abs(delta) >= this.state.width * this.props.items.length) {
        delta = 0;
        activeIndex = 0;
      } else {
        activeIndex++;
      }
    }

    this.setState({
      translateValue: delta,
      activeIndex: activeIndex,
    });
  };

  moveToIndex = index => {
    this.stopCarouselManually();

    if (index === this.state.activeIndex) {
      return false;
    }

    this.setState({
      activeIndex: index,
      translateValue: -1 * this.state.width * index,
    });
  };

  touchStartCallback = e => {
    this.setState({
      touchStart: e.touches[0].pageX,
    });
  };

  touchMoveCallback = e => {
    if (!this.animatingScroll) {
      let currentPosition = e.touches[0].pageX;
      if (this.state.touchStart > currentPosition + 50) {
        this.moveRightManually();
      } else if (this.state.touchStart < currentPosition - 50) {
        this.moveLeftManually();
      }
    }
  };

  transitionEndCallback = () => {
    this.animatingScroll = false;
  };

  isCarouselInViewport = () => {
    let carouselElement = document.getElementsByClassName(styles.carousel);
    if (carouselElement.length > 0) {
      var rect = carouselElement[0].getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth)
      );
    }

    return false;
  };
}

Carousel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      primaryImage: PropTypes.string,
      secondaryImage: PropTypes.string,
      description: PropTypes.string,
      url: PropTypes.string,
      urlClass: PropTypes.string,
    })
  ),
  clickHandler: PropTypes.func.isRequired,
};
